import React from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export function CuponesDialog({ authAxios, onSuccess }) {
  const [showNew, setShowNew] = React.useState(false);
  const [products, setProducts] = React.useState([]);
  const [form, setForm] = React.useState({
    code: "",
    discount_type: "percentage",
    discount_value: 0,
    min_order_value: 0,
    max_uses: 100,
    product_ids: [],
    pickup_only: false,
    online_only: false,
    max_quantity_per_order: 1
  });

  React.useEffect(() => {
    if (!showNew) return;
    authAxios.get("/menu/products")
      .then(res => setProducts((res.data || []).filter(p => p.active !== false && p.for_sale !== false)))
      .catch(() => setProducts([]));
  }, [showNew, authAxios]);

  const create = async () => {
    if (!form.code) {
      toast.error("Código obrigatório");
      return;
    }
    try {
      await authAxios.post("/marketing/coupons", form);
      toast.success("Cupom criado");
      setShowNew(false);
      setForm({
        code: "",
        discount_type: "percentage",
        discount_value: 0,
        min_order_value: 0,
        max_uses: 100,
        product_ids: [],
        pickup_only: false,
        online_only: false,
        max_quantity_per_order: 1
      });
      onSuccess();
    } catch {
      toast.error("Erro ao criar cupom");
    }
  };

  return (
    <div className="flex justify-end mb-4">
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogTrigger asChild>
          <Button size="lg" className="gap-2">
            <Plus className="w-4 h-4" />
            Novo Cupom
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Cupom</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Código</Label>
              <Input
                value={form.code}
                onChange={e => setForm({...form, code: e.target.value.toUpperCase()})}
                placeholder="KAISO20"
                className="font-mono uppercase"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentagem (%)</SelectItem>
                    <SelectItem value="fixed">Fixo (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Valor</Label>
                <Input
                  type="number"
                  value={form.discount_value}
                  onChange={e => setForm({...form, discount_value: Number(e.target.value)})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Produto da oferta</Label>
              <Select
                value={form.product_ids[0] || "all"}
                onValueChange={v => setForm({ ...form, product_ids: v === "all" ? [] : [v] })}
              >
                <SelectTrigger data-testid="coupon-product-select"><SelectValue placeholder="Todos os produtos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os produtos</SelectItem>
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} · €{Number(product.price || 0).toFixed(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.pickup_only}
                  onChange={e => setForm({ ...form, pickup_only: e.target.checked })}
                  data-testid="coupon-pickup-only"
                />
                Somente retirada
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.online_only}
                  onChange={e => setForm({ ...form, online_only: e.target.checked })}
                  data-testid="coupon-online-only"
                />
                Somente online
              </label>
            </div>
            <div className="space-y-2">
              <Label>Máximo por pedido</Label>
              <Input
                type="number"
                min="1"
                value={form.max_quantity_per_order}
                onChange={e => setForm({ ...form, max_quantity_per_order: Math.max(1, Number(e.target.value) || 1) })}
                data-testid="coupon-max-quantity"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pedido Mín. (€)</Label>
                <Input
                  type="number"
                  value={form.min_order_value}
                  onChange={e => setForm({...form, min_order_value: Number(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Usos Máx.</Label>
                <Input
                  type="number"
                  value={form.max_uses}
                  onChange={e => setForm({...form, max_uses: Number(e.target.value)})}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} className="w-full" size="lg">Criar Cupom</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
